import path from "path";

import { ensureDir, pathExists } from "fs-extra";

import { generatorFactory, getGeneratorSourceKey } from "../../BuildTasks/Generator/AcquireFormGenerator";
import { resolveGeneratorVersion, resolveLocalGeneratorSource } from "../../BuildTasks/PublishedVersionUtils";
import { resolveFormInfo } from "../../BuildTasks/ResolveFormInfo";
import { ConsoleApplicationContext } from "../../Commons/ConsoleApplication/ConsoleApplicationContext";
import { writeFileAsync } from "../../Commons/FsUtils";
import { Observe } from "../../Commons/TasksCore/Observe";
import { WellKnownDirectories } from "../../../../Common/WellKnownDirectories";

import { LocalizationOptions } from "./LocalizationOptions";

export async function runLocalization(
    context: ConsoleApplicationContext,
    options: LocalizationOptions,
    farmDirectory: string,
    engineDirectory: string,
    candyModulesDirectory: string
): Promise<void> {
    const formDirPath = path.join(farmDirectory, "forms", options.gfv);
    if (!(await pathExists(formDirPath))) {
        throw new Error(`GFV ${options.gfv} don't exist`);
    }

    const localizeChain = Observe.file(path.join(formDirPath, WellKnownDirectories.FormJsonFileName))
        .transform(formJsonFile =>
            resolveFormInfo(formJsonFile, formDirPath, context.logger.withPrefix(`FORM:${options.gfv}`))
        )
        .transform(async x => {
            if (options.useLocalGeneratorSources) {
                return {
                    ...x,
                    generatorSource: resolveLocalGeneratorSource(engineDirectory),
                };
            }

            if (x.generatorVersion == undefined) {
                throw new Error("generatorVersion is no defined");
            }

            return {
                ...x,
                generatorSource: await resolveGeneratorVersion(x.generatorVersion),
            };
        })
        .attach(
            async fi => generatorFactory(fi.generatorSource, candyModulesDirectory, false),
            async (fi, y) => ({ ...fi, generator: y }),
            async fi => getGeneratorSourceKey(fi.generatorSource)
        )
        .do(async fi => {
            const logger = context.logger.withPrefix("LOCALIZE").withPrefix(options.gfv);

            const formSource = await fi.generator.readSources({
                gfv: options.gfv,
                formPath: formDirPath,
                farmDirectory: farmDirectory,
            });

            if (options.dictionaryOnly) {
                logger.log("Generate translate dictionary");
                const localizationDictionary = fi.generator.getDictionary(formSource.sugarContent);
                logger.log("Write translate dictionary");
                await writeFilesBatch(localizationDictionary, formDirPath);
            } else {
                logger.log("Generate localization sugar");
                const localizedSugar = fi.generator.getLocalizedSugar(formSource.sugarContent);

                logger.log("Generate translate dictionary");
                const localizationDictionary = fi.generator.getDictionary(localizedSugar);

                const oldSugarDirectory = path.join(formDirPath, "oldsugar");
                logger.log(`Write previous version sugar in "${oldSugarDirectory}"`);
                await ensureDir(oldSugarDirectory);
                await Promise.all(
                    Object.entries(formSource.sugarContent).map(([filename, sugar]) =>
                        writeFileAsync(path.join(oldSugarDirectory, filename + ".sugar.xml"), sugar)
                    )
                );

                logger.log("Write localization sugar");
                await writeFilesBatch(localizedSugar, formDirPath);

                logger.log("Write translate dictionary");
                await writeFilesBatch(localizationDictionary, formDirPath);
                logger.log("Localize successful complete");
            }
        });

    await localizeChain.currentValue;
}

async function writeFilesBatch(
    filesBatch: {
        [relativeFileName: string]: string | Buffer;
    },
    outputDirectory: string
): Promise<void> {
    for (const filePath of Object.keys(filesBatch)) {
        const content = filesBatch[filePath];
        if (content != undefined && content !== "") {
            await ensureDir(path.join(outputDirectory, path.parse(filePath).dir));
            await writeFileAsync(path.join(outputDirectory, filePath), content);
        }
    }
}
